import {
  CloseOutlined,
  DownOutlined,
  LinkOutlined,
  UpOutlined
} from '@ant-design/icons';
import { Button, Dropdown, Input } from 'antd';
// eslint-disable-next-line no-use-before-define
import * as React from 'react';

import styles from './filters.module.css';
import { ProjectSearchFilters } from './tokens';

export type ProjectSearchPageFilterProps = {
  compact?: boolean;
  currentPageValue?: string;
  filters: ProjectSearchFilters;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  onChange: (filters: ProjectSearchFilters) => void;
};

export const normalizePageFilterValue = (value: string) => value.trim();

const PageFilter: React.FC<ProjectSearchPageFilterProps> = ({
  compact = false,
  currentPageValue,
  filters,
  getPopupContainer,
  onChange
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [url, setUrl] = React.useState('');
  const nextValue = normalizePageFilterValue(url);
  const isCurrentPage = !!currentPageValue && filters.page === currentPageValue;

  const setValue = (page: string) => {
    onChange({ ...filters, page });
    setUrl('');
  };

  const addValue = () => {
    if (!nextValue) return;
    setValue(nextValue);
  };

  const clearValue = () => setValue('');

  const pageHint = () => {
    if (filters.page) return 'Clear to filter on a different page';
    if (nextValue) return 'Press Enter to add';

    return (
      <>
        Full URL or a path like <code>/pricing</code>
      </>
    );
  };

  const overlay = (
    <div className={styles.pageDropdownPanel}>
      <div
        className={`${styles.pageFilterContent} ${
          compact ? styles.compactPageFilterContent : ''
        }`}
      >
        {filters.page ? (
          <div
            aria-label={`Page filter: ${filters.page}`}
            className={styles.pageValue}
          >
            <LinkOutlined aria-hidden="true" />
            <span className={styles.pageValueLabel}>
              {isCurrentPage ? 'This page' : filters.page}
            </span>
            <Button
              aria-label="Clear page filter"
              icon={<CloseOutlined />}
              onClick={clearValue}
              size="small"
              type="text"
            />
          </div>
        ) : (
          <>
            {currentPageValue && (
              <Button
                block
                className={styles.currentPageButton}
                icon={<LinkOutlined />}
                onClick={() => setValue(currentPageValue)}
                size="small"
              >
                This page
              </Button>
            )}
            <div className={styles.pageUrlInput}>
              <Input
                aria-label="Page URL or path"
                onChange={event => setUrl(event.target.value)}
                onPressEnter={addValue}
                placeholder="Paste a URL or type a path"
                prefix={<LinkOutlined />}
                value={url}
              />
              <Button disabled={!nextValue} onClick={addValue} type="primary">
                Add
              </Button>
            </div>
          </>
        )}
        <p className={styles.pageHint}>{pageHint()}</p>
      </div>
    </div>
  );

  return (
    <Dropdown
      arrow={{ pointAtCenter: true }}
      getPopupContainer={
        getPopupContainer || (trigger => trigger.parentElement || document.body)
      }
      onOpenChange={setIsOpen}
      open={isOpen}
      overlay={overlay}
      overlayClassName={`${styles.pageDropdownOverlay} ${
        compact ? styles.compactPageDropdownOverlay : ''
      }`}
      placement="bottomLeft"
      trigger={['click']}
    >
      <Button
        className={styles.filterPill}
        shape="round"
        size="small"
        type={isOpen || filters.page ? 'primary' : 'default'}
      >
        {filters.page ? 'Page (1)' : 'Page'}
        {isOpen ? <UpOutlined /> : <DownOutlined />}
      </Button>
    </Dropdown>
  );
};

export default PageFilter;
